if (!customElements.get('m-fbt-bundle')) {
  class MFbtBundle extends HTMLElement {
    constructor() {
      super();
      this._onChange = this._onChange.bind(this);
      this._handleAddToCart = this._handleAddToCart.bind(this);
      this._onLayoutChange = this._onLayoutChange.bind(this);
      this._swiper = null;
      this._carouselBreakpoint = 4;
      this._mobileQuery = window.matchMedia('(max-width: 767px)');
    }

    connectedCallback() {
      this.recommendationsContainer = this.querySelector('[data-fbt-recommendations]');
      this.listEl = this.querySelector('[data-fbt-list]');
      this.carouselControls = this.querySelector('[data-fbt-carousel-controls]');
      this.totalEl = this.querySelector('[data-fbt-total]');
      this.savingsEl = this.querySelector('[data-fbt-savings]');
      this.errorEl = this.querySelector('.m-fbt-bundle__error');
      this.addButton = this.querySelector('[data-fbt-add]');
      this.showSavings = this.dataset.showSavings === 'true';
      this.savingsPercent = parseFloat(this.dataset.savingsPercent || '0', 10);

      this.addEventListener('change', this._onChange);

      if (this.addButton) {
        this.addButton.addEventListener('click', this._handleAddToCart);
      }

      this._mobileQuery.addEventListener('change', this._onLayoutChange);
      document.addEventListener('matchMobile', this._onLayoutChange);
      document.addEventListener('unmatchMobile', this._onLayoutChange);

      this._observeRecommendations();
      this._setupMobileLayout();
      this.recalculateTotal();
    }

    disconnectedCallback() {
      this.removeEventListener('change', this._onChange);
      if (this.addButton) {
        this.addButton.removeEventListener('click', this._handleAddToCart);
      }
      if (this._observer) {
        this._observer.disconnect();
      }
      this._mobileQuery.removeEventListener('change', this._onLayoutChange);
      document.removeEventListener('matchMobile', this._onLayoutChange);
      document.removeEventListener('unmatchMobile', this._onLayoutChange);
      this._destroyCarousel();
    }

    _onLayoutChange() {
      this._setupMobileLayout();
    }

    _isMobile() {
      if (window.MinimogTheme?.config?.mqlMobile != null) {
        return window.MinimogTheme.config.mqlMobile;
      }
      return this._mobileQuery.matches;
    }

    _getListItems() {
      const items = [];
      if (!this.listEl) return items;

      const walk = (node) => {
        Array.from(node.children).forEach((child) => {
          if (child.matches('[data-fbt-card]')) {
            items.push({ type: 'card', el: child });
          } else if (child.classList.contains('fbt-card__plus')) {
            items.push({ type: 'plus', el: child });
          } else if (child.matches('[data-fbt-recommendations]')) {
            walk(child);
          }
        });
      };

      walk(this.listEl);
      return items;
    }

    _setupMobileLayout() {
      if (!this.listEl) return;

      const cardCount = this.getCards().length;
      this.dataset.fbtCount = cardCount;
      this.style.setProperty('--fbt-count', cardCount);

      const needsCarousel = this._isMobile() && cardCount > this._carouselBreakpoint;

      if (needsCarousel) {
        this._initCarousel();
      } else {
        this._destroyCarousel();
      }

      this.classList.toggle('m-fbt-bundle--carousel', needsCarousel);
    }

    _initCarousel() {
      if (!this.listEl || this._swiper || !window.MinimogLibs?.Swiper) return;

      const items = this._getListItems();
      if (!items.length) return;

      const wrapper = document.createElement('div');
      wrapper.className = 'swiper-wrapper';

      let pendingPlus = null;

      items.forEach((item) => {
        if (item.type === 'plus') {
          pendingPlus = item.el;
          return;
        }

        const slide = document.createElement('div');
        slide.className = 'swiper-slide m-fbt-bundle__slide';

        const inner = document.createElement('div');
        inner.className = 'm-fbt-bundle__slide-inner';

        if (pendingPlus) {
          inner.appendChild(pendingPlus);
          pendingPlus = null;
        }

        inner.appendChild(item.el);
        slide.appendChild(inner);
        wrapper.appendChild(slide);
      });

      if (this.recommendationsContainer) {
        this.recommendationsContainer.style.display = 'none';
      }

      this.listEl.classList.add('swiper-container', 'm-fbt-bundle__list--swiper');
      this.listEl.appendChild(wrapper);

      const paginationEl = this.carouselControls?.querySelector('.swiper-pagination');
      const prevEl = this.carouselControls?.querySelector('.swiper-button-prev');
      const nextEl = this.carouselControls?.querySelector('.swiper-button-next');

      this._swiper = new MinimogLibs.Swiper(this.listEl, {
        slidesPerView: 2.15,
        spaceBetween: 8,
        threshold: 2,
        watchOverflow: true,
        pagination: paginationEl
          ? {
              el: paginationEl,
              clickable: true,
            }
          : undefined,
        navigation:
          prevEl && nextEl
            ? {
                prevEl,
                nextEl,
              }
            : undefined,
        breakpoints: {
          480: {
            slidesPerView: 2.5,
          },
        },
      });

      if (this.carouselControls) {
        this.carouselControls.classList.remove('m:hidden');
      }
    }

    _destroyCarousel() {
      if (this._swiper) {
        this._swiper.destroy(true, true);
        this._swiper = null;
      }

      if (!this.listEl) return;

      const wrapper = this.listEl.querySelector('.swiper-wrapper');
      if (wrapper) {
        const orderedNodes = [];
        wrapper.querySelectorAll('.m-fbt-bundle__slide-inner').forEach((inner) => {
          Array.from(inner.childNodes).forEach((node) => {
            if (node.nodeType === 1) orderedNodes.push(node);
          });
        });

        wrapper.remove();

        const recContainer = this.recommendationsContainer;
        if (recContainer) {
          recContainer.innerHTML = '';
          recContainer.style.display = '';
        }

        orderedNodes.forEach((node) => {
          if (node.matches('.fbt-card--current')) {
            this.listEl.prepend(node);
          } else if (recContainer) {
            recContainer.appendChild(node);
          } else {
            this.listEl.appendChild(node);
          }
        });

        if (recContainer && !this.listEl.contains(recContainer)) {
          this.listEl.appendChild(recContainer);
        }
      }

      this.listEl.classList.remove('swiper-container', 'm-fbt-bundle__list--swiper');

      if (this.carouselControls) {
        this.carouselControls.classList.add('m:hidden');
      }
    }

    _observeRecommendations() {
      if (!this.recommendationsContainer || !this.recommendationsContainer.dataset.url) return;

      const loadRecommendations = () => {
        fetch(this.recommendationsContainer.dataset.url)
          .then((response) => response.text())
          .then((text) => {
            const html = generateDomFromString(text);
            const inner = html.querySelector('.m-fbt-bundle__recommendations-inner');
            if (inner && inner.innerHTML.trim().length) {
              this.recommendationsContainer.innerHTML = inner.innerHTML;
              this.classList.add('m-fbt-bundle--loaded');
              this._setupMobileLayout();
              this.recalculateTotal();
            } else {
              this.classList.add('m-fbt-bundle--no-recommendations');
              this._setupMobileLayout();
            }
          })
          .catch((error) => {
            console.error('FBT recommendations failed to load:', error);
            this.classList.add('m-fbt-bundle--no-recommendations');
          });
      };

      if ('IntersectionObserver' in window) {
        this._observer = new IntersectionObserver((entries, observer) => {
          if (!entries[0].isIntersecting) return;
          observer.unobserve(this);
          loadRecommendations();
        }, { rootMargin: '400px' });
        this._observer.observe(this);
      } else {
        loadRecommendations();
      }
    }

    _onChange(event) {
      const { target } = event;
      const card = target.closest('[data-fbt-card]');
      if (!card) return;

      if (target.matches('[data-fbt-checkbox]')) {
        this._onCheckboxChange(card, target);
        return;
      }

      if (target.matches('[data-fbt-variant-select]')) {
        this._onVariantChange(card, target);
      }
    }

    _onCheckboxChange(card, checkbox) {
      const checked = checkbox.checked;
      card.dataset.checked = checked ? 'true' : 'false';
      card.classList.toggle('fbt-card--excluded', !checked);
      this.recalculateTotal();
    }

    _onVariantChange(card, select) {
      const selectedOption = select.options ? select.options[select.selectedIndex] : null;
      if (!selectedOption) return;

      const price = parseInt(selectedOption.dataset.price || '0', 10);
      const available = selectedOption.dataset.available !== 'false';
      const variantId = select.value;

      card.dataset.variantId = variantId;
      card.dataset.checked = available ? card.dataset.checked : 'false';

      const priceEl = card.querySelector('[data-fbt-card-price]');
      if (priceEl) {
        priceEl.dataset.price = price;
        priceEl.textContent = formatMoney(price, window.MinimogSettings.money_format);
      }

      const checkbox = card.querySelector('[data-fbt-checkbox]');
      if (checkbox) {
        checkbox.disabled = !available;
        if (!available) {
          checkbox.checked = false;
          card.dataset.checked = 'false';
          card.classList.add('fbt-card--excluded');
        }
      }

      this.recalculateTotal();
    }

    getCards() {
      return Array.from(this.querySelectorAll('[data-fbt-card]'));
    }

    getCheckedCards() {
      return this.getCards().filter((card) => card.dataset.checked === 'true');
    }

    recalculateTotal() {
      const checkedCards = this.getCheckedCards();
      let total = 0;

      checkedCards.forEach((card) => {
        const priceEl = card.querySelector('[data-fbt-card-price]');
        const select = card.querySelector('[data-fbt-variant-select]');
        let price = 0;

        if (priceEl?.dataset?.price) {
          price = parseInt(priceEl.dataset.price, 10);
        } else if (select?.options) {
          price = parseInt(select.options[select.selectedIndex]?.dataset?.price || '0', 10);
        }

        total += price;
      });

      if (this.totalEl) {
        this.totalEl.textContent = formatMoney(total, window.MinimogSettings.money_format);
        this.totalEl.dataset.totalPrice = total;
      }

      if (this.addButton) {
        this.addButton.disabled = checkedCards.length === 0;
      }

      this._updateSavings(total, checkedCards.length);
    }

    _updateSavings(total, checkedCount) {
      if (!this.showSavings || !this.savingsEl || !this.savingsPercent) return;

      if (checkedCount < 2 || total <= 0) {
        this.savingsEl.style.display = 'none';
        this.savingsEl.textContent = '';
        return;
      }

      const savedAmount = Math.round(total * this.savingsPercent / 100);
      if (savedAmount <= 0) {
        this.savingsEl.style.display = 'none';
        return;
      }

      const formatted = formatMoney(savedAmount, window.MinimogSettings.money_format);
      this.savingsEl.textContent = `Save ${formatted} when you buy this bundle`;
      this.savingsEl.style.display = '';
    }

    _handleAddToCart(event) {
      event.preventDefault();

      const checkedCards = this.getCheckedCards();
      if (!checkedCards.length) return;

      const items = checkedCards.map((card) => {
        const idInput = card.querySelector('[name="id"]');
        return {
          id: idInput ? idInput.value : card.dataset.variantId,
          quantity: 1,
        };
      });

      let sectionsToBundle = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
      );

      const data = {
        items,
        sections: sectionsToBundle,
        sections_url: window.location.pathname,
      };

      const config = fetchConfig('javascript');
      config.method = 'POST';
      config.body = JSON.stringify(data);

      this._toggleLoading(true);

      const { MinimogSettings, MinimogStrings } = window;
      fetch(`${MinimogSettings.routes.cart_add_url}`, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            this._showError(response.description || 'Failed to add items to cart.');
            document.dispatchEvent(
              new CustomEvent('product-ajax:error', {
                detail: { errorMessage: response.description },
              })
            );
            return;
          }

          const cartDrawer = document.querySelector('m-cart-drawer');
          if (MinimogSettings.use_ajax_atc) {
            if (cartDrawer && MinimogSettings.enable_cart_drawer) {
              MinimogTheme.Notification.show({
                target: cartDrawer.querySelector('m-cart-items'),
                method: 'prepend',
                type: 'success',
                message: MinimogStrings.itemAdded,
                delay: 400,
              });
              cartDrawer.open();
            } else {
              MinimogTheme.Notification.show({
                target: this.errorEl || document.body,
                method: 'appendChild',
                type: 'success',
                message: MinimogStrings.itemAdded,
                last: 3000,
                sticky: !this.errorEl,
              });
            }

            window.MinimogEvents.emit(MinimogTheme.pubSubEvents.cartUpdate, { cart: response });
            document.dispatchEvent(
              new CustomEvent('product-ajax:added', {
                detail: { product: response },
              })
            );
          } else {
            window.location = MinimogSettings.routes.cart;
          }
        })
        .catch((error) => {
          console.error('FBT add to cart error:', error);
          this._showError('An error occurred while adding items to the cart.');
        })
        .finally(() => {
          this._toggleLoading(false);
        });
    }

    _showError(message) {
      MinimogTheme.Notification.show({
        target: this.errorEl || document.body,
        method: 'appendChild',
        type: 'warning',
        message,
      });
    }

    _toggleLoading(loading) {
      if (!this.addButton) return;
      this.addButton.classList.toggle('m-spinner-loading', loading);
      this.addButton.disabled = loading || this.getCheckedCards().length === 0;
    }
  }

  customElements.define('m-fbt-bundle', MFbtBundle);
}
